import React, { useEffect, useMemo, useState } from "react";
import { 
  ReturnsPanelStyle,
  ColumnWidthStyles,
  ModalStyles
} from "./Returns.styled";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store";
import { IApplicationState } from "../../store";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  FetchCapacityReturnsAction,
  HideReturnsPanelAction,
} from "../../store/Returns/ActionCreator";
import { canSubmitReturn } from "../../helper/disabled-button";
import { toast } from "react-toastify";

// Import custom hooks
import { useReturnsForm, useReturnsSubmission, useReturnsModals } from "./hooks";

// Import components
import { ConfirmationModal } from "./components/ConfirmationModal";
import { NoQuantityModal, ExceedsCapacityModal } from "./components/ValidationModals";
import { AccessDeniedModal } from "./components/AccessDeniedModal";
import { SourceAuctionsTable } from "./components/SourceAuctionsTable";
import { ReturnsHeader } from "./components/ReturnsHeaderFooter";
import { BookSelection } from "./components/BookSelection";
import {
  renderQuantityInput,
  renderEditButton
} from "./components/ReturnsCellRenderers";

// Import utilities
import { formatDeliveryPeriod } from "./utils";
import { 
  calculateMaxHeight, 
  calculateMaxWidth, 
  calculateTotalReturnCapacity,
  getAuctionIdFromName,
  calculatePanelHeight 
} from "./utils/calculations";

// Book state
export const noBookSelected = "00000000-0000-0000-0000-000000000000"; 

const Returns: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Basic state
  const [maxHeight, setMaxHeight] = useState(calculateMaxHeight());
  const [maxWidth] = useState(calculateMaxWidth());
  const [submitClicked, setSubmitClicked] = useState(false);
  const [confirmClicked, setConfirmClicked] = useState(false);
  
  // Redux selectors
  const profile = useSelector((state: IApplicationState) => state.profile.profile);
  const bookSelector = useSelector((state: IApplicationState) => state.book);
  const sourceAuctions = useSelector((state: IApplicationState) => state.returns.sourceAuctions);
  const auction = useSelector((state: IApplicationState) => state.returns.auction);
  const isLoading = useSelector((state: IApplicationState) => state.returns.isLoading);

  // Book state
  const noBookSelectedName = "Default Book";
  const [book, setBook] = useState({ bookCode: noBookSelected, bookName: noBookSelectedName });
  
  // Custom hooks
  const formState = useReturnsForm(sourceAuctions || []);
  const submissionState = useReturnsSubmission(auction);
  const modalState = useReturnsModals();
  
  const rowData = useMemo(() => {
    return sourceAuctions || [];
  }, [sourceAuctions]);

  // Calculate dynamic heights based on row count
  const tableHeight = useMemo(() => {
    if (rowData && rowData.length > 0) {
      // Each row is 48px, add 48px for header
      const baseHeight = (rowData.length + 1) * 48;
      return Math.max(150, Math.min(400, baseHeight)); // Min 150px, max 400px
    }
    return 150; // Default height for empty or loading state
  }, [rowData]);
  
  const panelHeight = useMemo(() => {
    return calculatePanelHeight(rowData ? rowData.length : 0);
  }, [rowData]);

  // Resize event handler
  useEffect(() => {
    const handleResize = () => {
      setMaxHeight(calculateMaxHeight());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch data when auction changes
  useEffect(() => {
    if (auction?.identification) {
      dispatch(FetchCapacityReturnsAction(auction));
    }
  }, [auction?.identification, dispatch]);

  // Effect to handle return submission response with toast
  useEffect(() => {
    // When submission completes successfully, close the panel automatically
    if (!submissionState.isSubmitting && submissionState.returnSubmissionResponse) {
      // Clear form state and close panel
      formState.setQuantities({});
      submissionState.setSelectedSourceAuctions({});
      dispatch(HideReturnsPanelAction());
    }
  }, [submissionState.isSubmitting, submissionState.returnSubmissionResponse, formState, submissionState, dispatch]);

  // Effect to handle source auction fetching errors
  useEffect(() => {
    // Source auction fetching errors - show toast instead of modal
    if (submissionState.errorMessage && !isLoading && !submissionState.isSubmitting) {
      toast.error(`Error fetching source auctions: ${submissionState.errorMessage}`, { 
        autoClose: 5000, 
        draggable: true 
      });
    }
  }, [submissionState.errorMessage, isLoading, submissionState.isSubmitting]);

  // Effect to re-enable panel when submission errors occur
  useEffect(() => {
    // Re-enable the panel when submission fails with an error
    if (submissionState.errorMessage && !submissionState.isSubmitting && submitClicked) {
      setSubmitClicked(false);
      setConfirmClicked(false);
    }
  }, [submissionState.errorMessage, submissionState.isSubmitting, submitClicked]);

  // Event handlers
  const onBookSelectionChanged = (book: { bookCode: string; bookName: string }) => {
    setBook(book);
  };

  const onClose = () => {
    setSubmitClicked(false);
    modalState.closeAllModals();
    return dispatch(HideReturnsPanelAction());
  };

  // Handler for submitting a return request
  const handleSubmitReturn = () => {
    if (submitClicked) return;  // ignore if already clicked
    // Check if user has permission to submit returns
    if (!canSubmitReturn(profile)) {
      modalState.setShowAccessDeniedModal(true);
      return;
    }
    setConfirmClicked(false);
    setSubmitClicked(true);

    submissionState.handleSubmitReturn(
      formState.quantities,
      rowData,
      formState.editingAuctionId,
      formState.activeInputRef,
      formState.getExceedingCapacityAuctions,
      formState.setQuantities,
      formState.setEditingAuctionId,
      modalState.setShowExceedsCapacityModal,
      modalState.setShowNoQuantityModal,
      modalState.setShowConfirmation
    );
  };

  // Handler for confirming a return submission
  const confirmSubmitReturn = async () => {
    submissionState.confirmSubmitReturn(
      formState.quantities,
      book.bookCode,
      modalState.setShowConfirmation
    );
  };

  // Rendering helpers
  const renderQuantityInputWrapper = (auctionId: string) => {
    return renderQuantityInput(
      auctionId,
      rowData,
      formState.editingAuctionId,
      formState.quantities,
      formState.handleSaveValue,
      formState.handleCancelEdit,
      formState.isCancelling,
      formState.activeInputRef,
      formState.handleStartEdit,
      canSubmitReturn(profile)
    );
  };
  
  const renderEditButtonWrapper = (name: string) => {
    return renderEditButton(
      name,
      rowData,
      getAuctionIdFromName,
      formState.editingAuctionId,
      formState.activeInputRef,
      formState.handleSaveValue,
      formState.handleCancelEdit,
      formState.handleStartEdit,
      formState.setIsCancelling,
      canSubmitReturn(profile),
      handleSubmitReturn,
      formState.quantities
    );
  };

  const sourceAuctionValues = Object.values(submissionState.selectedSourceAuctions);
  const totalReturnCapacity = calculateTotalReturnCapacity(formState.quantities);

  return (
      <div data-testid="ReturnsPanel">
        {/* Apply global styles */}
        <ColumnWidthStyles />
        <ModalStyles />
        
        <ReturnsPanelStyle dynamicHeight={panelHeight} disabled={submitClicked}>
          <ReturnsHeader 
            title={auction?.auctionName || ""} 
            onClose={onClose} 
          />
          
          {/* Book Selection moved from footer to below header */}
          <BookSelection
            book={book}
            auction={auction}
            onBookSelectionChanged={onBookSelectionChanged}
            isLoading={bookSelector?.loading || isLoading}
          />

          <SourceAuctionsTable
            rowData={rowData}
            isLoading={isLoading}
            maxHeight={maxHeight}
            maxWidth={maxWidth}
            renderQuantityInput={renderQuantityInputWrapper}
            renderEditButton={renderEditButtonWrapper}
            tableHeight={tableHeight} // Pass calculated table height
          />
        </ReturnsPanelStyle>

        {/* Modals */}
        <ConfirmationModal
          open={modalState.showConfirmation}
          onCancel={() => {
            // If user cancelled (never confirmed), re-enable Submit
            if (!confirmClicked) {
              setSubmitClicked(false);
            }
            setConfirmClicked(false);
            modalState.setShowConfirmation(false);
          }}
          onConfirm={() => {
            setConfirmClicked(true);
            setSubmitClicked(true);
            confirmSubmitReturn();
          }}
          targetAuction={auction?.auctionName}
          sourceAuction={sourceAuctionValues[0]?.auctionName}
          totalCapacity={totalReturnCapacity}
          book={book}
          deliveryPeriod={sourceAuctionValues.length > 0 
            ? formatDeliveryPeriod(
                sourceAuctionValues[0]?.deliveryPeriodStart,
                sourceAuctionValues[0]?.deliveryPeriodEnd
              )
            : undefined
          }
        />

        <NoQuantityModal
          open={modalState.showNoQuantityModal}
          onClose={() => modalState.setShowNoQuantityModal(false)}
        />

        <ExceedsCapacityModal
          open={modalState.showExceedsCapacityModal}
          onClose={() => modalState.setShowExceedsCapacityModal(false)}
          editingAuctionId={formState.editingAuctionId}
          rowData={rowData}
        />

        <AccessDeniedModal
          open={modalState.showAccessDeniedModal}
          onClose={() => modalState.setShowAccessDeniedModal(false)}
        />
      </div>
  );
};

export default Returns;
